import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Disc3, Music, DollarSign, Upload, Trash2, ShieldHalf, Mail, LockKeyhole } from "lucide-react";

const Field = ({ label, children }) => (
  <div>
    <label className="overline text-xs text-muted-foreground mb-1 block">{label}</label>
    {children}
  </div>
);
const inputCls = "h-11 bg-transparent border-2 border-border focus:border-primary rounded-none text-white";

function FileUpload({ label, accept, fileId, onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.file_id);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    }
    setBusy(false);
  };
  return (
    <Field label={label}>
      <label className="flex items-center gap-3 border-2 border-dashed border-border hover:border-primary p-3 cursor-pointer transition-colors">
        <Upload size={18} className="text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{busy ? "Uploading..." : fileId ? "✓ Uploaded — replace" : "Choose file"}</span>
        <input data-testid={testid} type="file" accept={accept} onChange={handle} className="hidden" />
      </label>
      {fileId && accept.includes("image") && <img src={fileUrl(fileId)} alt="" className="w-20 h-20 object-cover border border-border mt-2" />}
    </Field>
  );
}

export default function Admin() {
  const { adminLock } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);

  // track form
  const [tForm, setTForm] = useState({ title: "", artist: "", album_id: "none", genre: "", audio_file_id: null, cover_file_id: null });
  // album form
  const [aForm, setAForm] = useState({ title: "", artist: "", description: "", year: "", cover_file_id: null });

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/albums").then((r) => setAlbums(r.data)).catch(() => {});
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const submitTrack = async (e) => {
    e.preventDefault();
    if (!tForm.audio_file_id) { toast.error("Upload an audio file first"); return; }
    try {
      await api.post("/tracks", {
        title: tForm.title, artist: tForm.artist, genre: tForm.genre,
        audio_file_id: tForm.audio_file_id, cover_file_id: tForm.cover_file_id,
        album_id: tForm.album_id === "none" ? null : tForm.album_id,
      });
      toast.success("Track published");
      setTForm({ title: "", artist: "", album_id: "none", genre: "", audio_file_id: null, cover_file_id: null });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const submitAlbum = async (e) => {
    e.preventDefault();
    try {
      await api.post("/albums", {
        title: aForm.title, artist: aForm.artist, description: aForm.description,
        cover_file_id: aForm.cover_file_id, year: aForm.year ? parseInt(aForm.year) : null,
      });
      toast.success("Album created");
      setAForm({ title: "", artist: "", description: "", year: "", cover_file_id: null });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const delTrack = async (id) => { await api.delete(`/tracks/${id}`); toast.success("Track deleted"); load(); };
  const delAlbum = async (id) => { await api.delete(`/albums/${id}`); toast.success("Album deleted"); load(); };

  const StatCard = ({ icon: Icon, label, value }) => (
    <div className="bg-card border border-border p-5">
      <Icon size={20} className="text-primary mb-3" />
      <div className="font-display text-3xl font-bold">{value ?? "—"}</div>
      <div className="overline text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );

  return (
    <div className="px-6 md:px-12 py-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline text-xs text-accent mb-2 flex items-center gap-2"><ShieldHalf size={14} /> Control Room</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-8">ADMIN</h1>
        </div>
        <button data-testid="admin-lock-btn" onClick={() => { adminLock(); nav("/"); }} className="flex items-center gap-2 border-2 border-border hover:border-primary px-4 py-2 text-xs overline font-bold transition-colors">
          <LockKeyhole size={14} /> Lock
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Users} label="Users" value={stats?.users} />
        <StatCard icon={DollarSign} label="Premium Subs" value={stats?.premium_users} />
        <StatCard icon={Music} label="Tracks" value={stats?.tracks} />
        <StatCard icon={Disc3} label="Albums" value={stats?.albums} />
      </div>

      <Tabs defaultValue="track">
        <TabsList className="bg-card border border-border rounded-none p-0 h-auto mb-6 flex-wrap">
          {["track", "album", "manage", "users", "messages"].map((v) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white overline text-xs px-5 py-3">
              {v === "track" ? "Upload Track" : v === "album" ? "New Album" : v === "messages" ? `Messages (${contacts.length})` : v}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* UPLOAD TRACK */}
        <TabsContent value="track">
          <form onSubmit={submitTrack} className="bg-card border border-border p-6 max-w-2xl space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Track Title"><Input data-testid="track-title" value={tForm.title} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} required className={inputCls} /></Field>
              <Field label="Artist"><Input data-testid="track-artist" value={tForm.artist} onChange={(e) => setTForm({ ...tForm, artist: e.target.value })} required className={inputCls} /></Field>
              <Field label="Genre"><Input data-testid="track-genre" value={tForm.genre} onChange={(e) => setTForm({ ...tForm, genre: e.target.value })} className={inputCls} /></Field>
              <Field label="Album">
                <Select value={tForm.album_id} onValueChange={(v) => setTForm({ ...tForm, album_id: v })}>
                  <SelectTrigger data-testid="track-album-select" className={inputCls}><SelectValue placeholder="Standalone" /></SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-none text-white">
                    <SelectItem value="none">Standalone (no album)</SelectItem>
                    {albums.map((a) => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <FileUpload label="Audio File (mp3/wav)" accept="audio/*" fileId={tForm.audio_file_id} onUploaded={(id) => setTForm({ ...tForm, audio_file_id: id })} testid="track-audio-upload" />
            <FileUpload label="Cover Art (optional)" accept="image/*" fileId={tForm.cover_file_id} onUploaded={(id) => setTForm({ ...tForm, cover_file_id: id })} testid="track-cover-upload" />
            <Button data-testid="track-submit" type="submit" className="bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline h-12 px-8">Publish Track</Button>
          </form>
        </TabsContent>

        {/* NEW ALBUM */}
        <TabsContent value="album">
          <form onSubmit={submitAlbum} className="bg-card border border-border p-6 max-w-2xl space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Album Title"><Input data-testid="album-title" value={aForm.title} onChange={(e) => setAForm({ ...aForm, title: e.target.value })} required className={inputCls} /></Field>
              <Field label="Artist"><Input data-testid="album-artist" value={aForm.artist} onChange={(e) => setAForm({ ...aForm, artist: e.target.value })} required className={inputCls} /></Field>
              <Field label="Year"><Input data-testid="album-year" type="number" value={aForm.year} onChange={(e) => setAForm({ ...aForm, year: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Description"><Textarea data-testid="album-description" value={aForm.description} onChange={(e) => setAForm({ ...aForm, description: e.target.value })} className="bg-transparent border-2 border-border focus:border-primary rounded-none text-white" /></Field>
            <FileUpload label="Cover Art" accept="image/*" fileId={aForm.cover_file_id} onUploaded={(id) => setAForm({ ...aForm, cover_file_id: id })} testid="album-cover-upload" />
            <Button data-testid="album-submit" type="submit" className="bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline h-12 px-8">Create Album</Button>
          </form>
        </TabsContent>

        {/* MANAGE */}
        <TabsContent value="manage">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h3 className="overline text-xs text-muted-foreground mb-3">Tracks ({tracks.length})</h3>
              <div className="border border-border">
                {tracks.length === 0 && <div className="p-6 text-sm text-muted-foreground">No tracks.</div>}
                {tracks.map((t) => (
                  <div key={t.id} data-testid={`manage-track-${t.id}`} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0">
                    <div className="min-w-0"><div className="font-display font-medium truncate">{t.title}</div><div className="text-xs text-muted-foreground truncate">{t.artist}</div></div>
                    <button data-testid={`delete-track-${t.id}`} onClick={() => delTrack(t.id)} className="text-muted-foreground hover:text-primary p-2"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="overline text-xs text-muted-foreground mb-3">Albums ({albums.length})</h3>
              <div className="border border-border">
                {albums.length === 0 && <div className="p-6 text-sm text-muted-foreground">No albums.</div>}
                {albums.map((a) => (
                  <div key={a.id} data-testid={`manage-album-${a.id}`} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0">
                    <div className="min-w-0"><div className="font-display font-medium truncate">{a.title}</div><div className="text-xs text-muted-foreground truncate">{a.artist} · {a.track_count} tracks</div></div>
                    <button data-testid={`delete-album-${a.id}`} onClick={() => delAlbum(a.id)} className="text-muted-foreground hover:text-primary p-2"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users">
          <div className="border border-border">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0">
                <div><div className="font-display font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></div>
                <div className="flex gap-2">
                  {u.role === "admin" && <span className="overline text-[10px] bg-accent text-black px-2 py-1">ADMIN</span>}
                  {u.is_premium && <span className="overline text-[10px] bg-primary text-white px-2 py-1">PREMIUM</span>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* MESSAGES */}
        <TabsContent value="messages">
          <div className="space-y-3">
            {contacts.length === 0 && <div className="border-2 border-dashed border-border p-12 text-center text-muted-foreground text-sm">No messages yet.</div>}
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-msg-${c.id}`} className="bg-card border border-border p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-display font-semibold">{c.name} <span className="text-muted-foreground text-sm font-normal">· {c.subject}</span></div>
                    <a href={`mailto:${c.email}`} className="text-xs text-primary hover:underline">{c.email}</a>
                  </div>
                  <button data-testid={`delete-contact-${c.id}`} onClick={() => delContact(c.id)} className="text-muted-foreground hover:text-primary p-1 shrink-0"><Trash2 size={16} /></button>
                </div>
                <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap">{c.message}</p>
                <div className="overline text-[10px] text-muted-foreground mt-3">{new Date(c.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
